import { css, radioClasses as muiRadioClasses, styled } from '@mui/material';
import MuiRadio from '@mui/material/Radio';

import { TooltipContainer } from '../TooltipContainer';
import { RadioChecked, RadioUnchecked } from './icons';
import { radioClasses } from './theme';

import type { Theme } from '@mui/material/styles';
import type { RadioOwnerState, RadioProps } from './types';

export function Radio(props: RadioProps) {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 props 구조분해에서 처리
  const {
    className,
    color = 'default',
    selectorOverrides,
    slotProps,
    disabledTooltip,
    disabled,
    error,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...others
  } = props;
  const { active = '&:active', hover = '&:hover' } = selectorOverrides ?? {};
  const displayError = !disabled && error;

  const ownerState: RadioOwnerState = {
    ...props,
    color,
    error: displayError,
    selectorOverrides: { ...selectorOverrides, active, hover },
  };

  // MUI Radio의 root는 role이 없는 `span`이라 여기에 aria-label/labelledby/describedby가 붙으면
  // 금지된 속성(aria-prohibited-attr)이 되고, 정작 실제 컨트롤인 내부 `<input type="radio">`는
  // 접근 가능한 이름이 없는 상태로 남는다. 접근성 속성은 root가 아니라 input 슬롯으로 전달한다.
  const ariaProps = {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  };
  const externalInputSlotProps = slotProps?.input;
  const mergedSlotProps: RadioProps['slotProps'] = {
    ...slotProps,
    // 호출부가 직접 넘긴 input slotProps가 우선하도록 뒤에 병합한다.
    input:
      typeof externalInputSlotProps === 'function'
        ? inputOwnerState => ({ ...ariaProps, ...externalInputSlotProps(inputOwnerState) })
        : { ...ariaProps, ...externalInputSlotProps },
  };

  return (
    // TooltipContainer 의 기본 span(display: inline-block)은 내부에 line box 를 만들어,
    // 상속된 line-height 가 라디오 아이콘보다 클 때 아이콘이 baseline 기준으로 아래로 밀린다.
    // inline-flex 로 감싸면 line box 없이 아이콘 높이만 차지해 옆 텍스트와 세로 중앙이 맞는다.
    <TooltipContainer display="inline-flex" message={disabled ? disabledTooltip : undefined}>
      <RadioRoot
        checkedIcon={<RadioChecked />}
        className={className}
        disableFocusRipple
        disableRipple
        disableTouchRipple
        icon={<RadioUnchecked />}
        ownerState={ownerState}
        disabled={disabled}
        slotProps={mergedSlotProps}
        {...others}
      />
    </TooltipContainer>
  );
}

Radio.displayName = 'Radio';

// Styled Components - theme.ts에서 이전된 스타일
const RadioRoot = styled(MuiRadio, {
  target: radioClasses.root,
})<{ ownerState: RadioOwnerState }>(({ theme, ownerState }) => {
  const { color } = ownerState;

  return css`
    /* theme.ts에서 이전된 기본 스타일 */
    font-size: 16px;
    padding: 0;

    & > svg {
      width: 1em;
      height: 1em;
      vertical-align: -0.125em;
    }

    /* color variants에 따른 스타일 */
    ${getColorVariantStyles(color, ownerState, theme)}
  `;
});

// Helper 함수: color variant에 따른 스타일 반환
function getColorVariantStyles(
  color: RadioOwnerState['color'],
  ownerState: RadioOwnerState,
  theme: Theme,
) {
  const { selectorOverrides, error } = ownerState;

  if (color === 'global') {
    return css`
      ${radioColor(
        theme.color['interactive/global/default'],
        '#F6F7F9', // FIXME (@walter-24) `theme.color['interactive/global/strongest/default']` 이 컬러값이 피그마와 싱크가 안되어 있음
        theme.color['interactive/global/danger/default'],
        theme.color['interactive/global/weakest/default'],
      )({ error })}

      ${selectorOverrides?.hover ?? '&:hover'} {
        ${radioColor(
          theme.color['interactive/global/hover'],
          theme.color['interactive/global/strongest/hover'],
          theme.color['interactive/global/danger/hover'],
          theme.color['interactive/global/weakest/default'],
        )({ error })}
      }

      ${selectorOverrides?.active ?? '&:active'} {
        ${radioColor(
          theme.color['interactive/global/press'],
          theme.color['interactive/global/strongest/press'],
          theme.color['interactive/global/danger/press'],
          theme.color['interactive/global/weakest/default'],
        )({ error })}
      }

      ${theme.mixins.focusRing('outside', {
        inverse: true,
        borderRadius: 16,
        selectors: { focus: `&.Mui-focusVisible` },
      })};

      &.${muiRadioClasses.disabled} {
        ${radioColor(
          theme.color['interactive/global/disabled'],
          theme.color['interactive/global/strongest/disabled'],
          theme.color['interactive/global/danger/disabled'],
          theme.color['interactive/global/weakest/disabled'],
        )({ error })}
      }
    `;
  }

  // default color
  return css`
    ${radioColor(
      theme.color['interactive/default'],
      theme.color['interactive/strongest/default'],
      theme.color['interactive/danger/default'],
    )({ error })}

    ${selectorOverrides?.hover ?? '&:hover'} {
      ${radioColor(
        theme.color['interactive/hover'],
        theme.color['interactive/strongest/hover'],
        theme.color['interactive/danger/hover'],
      )({ error })}
    }

    ${selectorOverrides?.active ?? '&:active'} {
      ${radioColor(
        theme.color['interactive/press'],
        theme.color['interactive/strongest/press'],
        theme.color['interactive/danger/press'],
      )({ error })}
    }

    ${theme.mixins.focusRing('outside', {
      borderRadius: 16,
      selectors: { focus: `&.Mui-focusVisible` },
    })};

    &.${muiRadioClasses.disabled} {
      ${radioColor(
        theme.color['interactive/disabled'],
        theme.color['interactive/strongest/disabled'],
        theme.color['interactive/danger/disabled'],
      )({ error })}
    }
  `;
}

// Helper 함수: radioColor (theme.ts에서 이전)
const radioColor =
  (interactive: string, strongest: string, danger: string, background?: string) =>
  ({ error }: { error?: boolean }) => css`
    --radio-background-color: ${background ?? 'white'};
    --radio-foreground-color: ${error ? danger : interactive};
    &.${muiRadioClasses.checked} {
      --radio-foreground-color: ${error ? danger : strongest};
    }
  `;
